import { reverseGeocode } from '@/api/calls/maps-calls/reverse-geo-code-calls'
import Charger from '@/classes/charger_classes/charger'
import type NetworkBranding from '@/classes/charger_classes/networkBranding'

/**
 * Takes a charger object and fills in missing address and name fields.
 * If the charger's coordinates are missing or invalid, an error is thrown.
 * If the charger's address is missing, it is reverse geocoded to get the address,
 * and the city, postal code, and country are filled in.
 * If the charger's name is missing, it is set to the branding name (if any)
 * plus the address (if any) or 'unknown'.
 *
 * @param {Charger} charger the charger object to be edited.
 * @param {NetworkBranding} [branding] the branding object to be used for filling in missing information.
 * @returns {Charger} the edited charger object.
 * @throws {Error} if the charger's coordinates are missing or invalid.
 */
export async function fillChargerMissingData(charger: Charger, branding?: NetworkBranding) {
  // guard clause vs missing or invalid coordinates
  if (!charger.coordinates || !charger.coordinates.latitude || !charger.coordinates.longitude) {
    throw new Error('Charger coordinates are missing or invalid')
  }

  // copy charger object for editing.
  const chargerCopy = new Charger({ ...charger })

  // check address is completed.
  if (!chargerCopy.address) {
    // reverse geocode coordinates to get address.
    const addressData = await reverseGeocode(
      chargerCopy.coordinates.latitude,
      chargerCopy.coordinates.longitude,
    )
    if (addressData && addressData.address) {
      chargerCopy.address =
        (addressData.address.house_number ? addressData.address.house_number + ' ' : '') +
        (addressData.address.road ?? '')
      chargerCopy.city = addressData.address.city
      chargerCopy.postalCode = addressData.address.postcode
      chargerCopy.state = addressData.address.state
      chargerCopy.country = addressData.address.country
      // check name exists.
      if (!chargerCopy.name) {
        chargerCopy.name =
          (branding && branding.displayName ? branding.displayName + ' ' : '') +
          (addressData?.address.road || 'unknown')
      }
    }
  }

  // check name exists.
  if (!chargerCopy.name) {
    chargerCopy.name =
      (branding && branding.displayName ? branding.displayName + ' ' : '') +
      (chargerCopy.address ? chargerCopy.address : 'unknown')
  }

  return chargerCopy
}
